// Web-push client helpers: capability checks, subscribe/unsubscribe, and a
// self-test trigger. The heavy lifting (showing notifications) lives in the
// service worker; this file just manages the browser subscription and syncs it
// to the server.

const API = process.env.REACT_APP_API_URL || '';

export function pushSupported() {
  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export function isStandalone() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

export function isIOS() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;
}

// VAPID public keys are URL-safe base64; the PushManager wants a Uint8Array.
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

// Current local state: is a subscription present, and what's the permission.
export async function getPushState() {
  if (!pushSupported()) return { supported: false, subscribed: false, permission: 'unsupported' };
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    return { supported: true, subscribed: !!sub, permission: Notification.permission };
  } catch {
    return { supported: true, subscribed: false, permission: Notification.permission };
  }
}

// Ask permission, create a subscription, and register it server-side.
export async function enablePush() {
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error(
      permission === 'denied'
        ? 'Notifications are blocked. Turn them on for OlogyHQ in your device/browser settings, then try again.'
        : 'Notification permission was not granted.'
    );
  }
  const reg = await navigator.serviceWorker.ready;
  const { publicKey } = await fetch(`${API}/api/push/public-key`, { credentials: 'include' }).then((r) => r.json());
  if (!publicKey) throw new Error('Push is not configured on the server yet.');

  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
  }

  const res = await fetch(`${API}/api/push/subscribe`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ subscription: sub.toJSON() }),
  });
  if (!res.ok) throw new Error('Could not save the subscription on the server.');
  return true;
}

// Remove the subscription both locally and server-side.
export async function disablePush() {
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (sub) {
    await fetch(`${API}/api/push/unsubscribe`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ endpoint: sub.endpoint }),
    }).catch(() => {});
    await sub.unsubscribe().catch(() => {});
  }
  return true;
}

// Ask the server to push a test to this user's devices. delaySeconds lets you
// background the app first and confirm it still arrives.
export async function sendPushTest(delaySeconds = 0) {
  const res = await fetch(`${API}/api/push/test`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ delaySeconds }),
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).message || 'Could not send the test.');
  return res.json();
}
